import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { formIO } from "../../../shared/src/codecs/types/formIO";
import { required } from "../../../shared/src/codecs/types/required";
import { uuid } from "../../../shared/src/codecs/types/uuid";
import { string } from "../../../shared/src/codecs/types/string";
import { integer } from "../../../shared/src/codecs/types/integer";
import { decimal } from "../../../shared/src/codecs/types/decimal";
import { boolean } from "../../../shared/src/codecs/types/boolean";
import { union } from "../../../shared/src/codecs/types/union";
import { nullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { literal } from "../../../shared/src/codecs/types/literal";
import { longString } from "../../../shared/src/codecs/types/longString";
import { array } from "../../../shared/src/codecs/types/array";

const ns = () => union([nullCodec(), string()]);
const ni = () => union([nullCodec(), integer()]);
const nd = () => union([nullCodec(), decimal()]);

export const ValuationTabStepName = () => union([
    literal("resolve_address"),
    literal("chimnie"),
    literal("propertydata_comps"),
    literal("rightmove_comps"),
    literal("compute_valuation"),
]);

export const ValuationTabStepStatus = () => union([
    literal("pending"),
    literal("succeeded"),
    literal("failed"),
    literal("skipped"),
]);

export const ValuationTabComparableSource = () => union([
    literal("propertydata"),
    literal("rightmove"),
]);

export const ValuationTabFactUsed = () => union([
    literal("ours"),
    literal("declared"),
    literal("predicted"),
    literal("nothing"),
]);

const ValuationTabStep = required({
    step: ValuationTabStepName(),
    status: ValuationTabStepStatus(),
    attempts: integer(),
    last_error: ns(),
    completed_at: ns(),
});

const ValuationTabSignal = required({
    kind: string(),
    value_pence: integer(),
    weight: decimal(),
});

const ValuationTabComparable = required({
    source: ValuationTabComparableSource(),
    address: ns(),
    postcode: ns(),
    price_pence: ni(),
    bedrooms: ni(),
    floor_area_sqm: nd(),
    property_type: ns(),
    sold_date: ns(),
    market_status: ns(),
    distance_km: nd(),
    relevance_score: nd(),
});

const ValuationTabVersion = required({
    version_id: uuid(),
    status: string(),
    recommended_value_pence: ni(),
    confidence_score: nd(),
    confidence_band: ns(),
    engine_version: string(),
    created_at: string(),
});

const ValuationTabFact = required({
    field: string(),
    ours: ns(),
    provider_declared: ns(),
    provider_predicted: ns(),
    used: ValuationTabFactUsed(),
    conflicts: boolean(),
});

export const ListingValuationTabFormIO = formIO(
    required({
        listing_id: uuid(),
    }),
    required({
        // ---- where it's up to (all derived; nothing new to keep in sync) ----
        // Stage comes from the listing's existing date columns, house style, so it
        // cannot disagree with `listings.status`.
        valuation_date: ns(),
        valuation_sent: boolean(),
        valuation_date_deferred: ns(),
        valuation_sent_deferred: ns(),
        listing_status: string(),
        do_not_contact_until: ns(),
        subject_property_type: ns(),
        reason_for_sale: ns(),
        subject_bedrooms: ni(),

        market_price_pence: ni(),
        to_achieve_pence: ni(),
        low_range_pence: ni(),
        high_range_pence: ni(),
        auction_price_pence: ni(),
        refurbished_pence: ni(),
        recommended_tier: ns(),
        confidence_score: nd(),
        confidence_band: ns(),
        date_of_death_pence: ni(),
        ineligible_reason: ns(),
        valued_at: ns(),

        is_stale: boolean(),

        signals: array(ValuationTabSignal),
        clip_applied: ns(),
        hpi_uplift_pct: nd(),
        hpi_fell_back_to_national: boolean(),
        comps_count: ni(),

        steps: array(ValuationTabStep),
        facts: array(ValuationTabFact),
        comparables: array(ValuationTabComparable),
        versions: array(ValuationTabVersion),

        provider_facts_updated_at: ns(),
        // Where the comparables came from and when. Without it the comps card and the
        // step list contradict each other whenever today's fetch has failed.
        comps_fetched_at: ns(),
        rationale: union([nullCodec(), longString()]),
        amended_reason: union([nullCodec(), longString()]),
    }),
);

export type TListingValuationTabFormIOCodec = typeof ListingValuationTabFormIO;
export type TListingValuationTabFormIO = TTypeOfCodec<TListingValuationTabFormIOCodec>;
export type TListingValuationTabOutput = TListingValuationTabFormIO["output"];
export type TValuationTabStep = TTypeOfCodec<typeof ValuationTabStep>;
export type TValuationTabSignal = TTypeOfCodec<typeof ValuationTabSignal>;
export type TValuationTabComparable = TTypeOfCodec<typeof ValuationTabComparable>;
export type TValuationTabVersion = TTypeOfCodec<typeof ValuationTabVersion>;
export type TValuationTabFact = TTypeOfCodec<typeof ValuationTabFact>;
